/**
 * Promo Tracker - Handles URL flags for promotional campaigns
 * Detects URL parameters like ?FacebookAds=true and sets promotional cookies
 * 
 * USAGE:
 * 
 * 1. Basic usage (automatic initialization):
 *    Just include this script and it will work with default settings
 * 
 * 2. Custom configuration:
 *    Add this BEFORE the promo-tracker.js script:
 *    <script>
 *      window.promoTrackerConfig = {
 *        discountCode: 'MYCUSTOMCODE',
 *        sourceDiscounts: {
 *          'FacebookAds': 'FBFREE',
 *          'GoogleAds': 'GOOGLEFREE', 
 *          'InstagramAds': 'IGFREE'
 *        },
 *        urlFlags: ['FacebookAds', 'MyCustomFlag'],
 *        debug: false
 *      };
 *    </script>
 * 
 * 3. Manual control:
 *    window.promoTracker.clearPromoData(); // Clear promo
 *    window.promoTracker.getPromoStatus(); // Check status
 * 
 * URL EXAMPLES:
 * - yourstore.com/products/shirt?FacebookAds=true → Sets promo cookie
 * - yourstore.com/products/shirt?GoogleAds=campaign123 → Sets promo cookie  
 * - yourstore.com/?promocode=special → Sets promo cookie
 * 
 * FLOW:
 * 1. User visits with URL flag (?FacebookAds=true)
 * 2. Cookie set with promo source
 * 3. Other scripts (like product-form) can check for cookie and apply discounts
 */

class PromoTracker {
  constructor(customConfig = {}) {
    this.config = {
      // URL parameter names to watch for
      urlFlags: ['FacebookAds', 'GoogleAds', 'InstagramAds', 'promocode'],
      // Cookie settings
      cookieName: 'promo_source',
      cookieExpireDays: 30,
      // Discount code to apply (you can customize this)
      discountCode: 'FREEOFFER',
      // Debug mode
      debug: true,
      // Whether to show promo banner
      showBanner: true,
      // Custom discount codes for different sources
      sourceDiscounts: {
        'FacebookAds': 'FBFREE',
        'GoogleAds': 'GOOGLEFREE',
        'InstagramAds': 'IGFREE',
        'promocode': 'FREEOFFER'
      },
      // Override any config
      ...customConfig
    };
    
    this.init();
  }

  /**
   * Initialize the promo tracker
   */
  init() {
    this.log('PromoTracker initialized');
    
    // Check URL parameters on page load
    this.checkUrlFlags();
    
    // Check if user already has promo cookie
    this.checkExistingPromo();
  }

  /**
   * Check URL for promotional flags
   */
  checkUrlFlags() {
    const urlParams = new URLSearchParams(window.location.search);
    
    for (const flag of this.config.urlFlags) {
      const flagValue = urlParams.get(flag);
      
      if (flagValue !== null) {
        this.log(`Found URL flag: ${flag}=${flagValue}`);
        this.setPromoSource(flag, flagValue);
        break; // Only process the first found flag
      }
    }
  }

  /**
   * Set promotional source in cookie
   */
  setPromoSource(source, value = 'true') {
    const promoData = {
      source: source,
      value: value,
      timestamp: Date.now(),
      applied: false
    };
    
    this.setCookie(this.config.cookieName, JSON.stringify(promoData), this.config.cookieExpireDays);
    this.log(`Promo source set: ${source}`);
    
    // Dispatch custom event
    document.dispatchEvent(new CustomEvent('promoApplied', {
      detail: promoData
    }));
  }

  /**
   * Check if user has existing promo cookie
   */
  checkExistingPromo() {
    const promoData = this.getPromoData();
    
    if (promoData) {
      this.log(`Existing promo found: ${promoData.source}`);
      // Promo exists - cart logic will handle discount application
    }
  }

  /**
   * Get promo data from cookie
   */
  getPromoData() {
    const cookieValue = this.getCookie(this.config.cookieName);
    
    if (cookieValue) {
      try {
        return JSON.parse(cookieValue);
      } catch (e) {
        this.log('Error parsing promo cookie:', e);
        return null;
      }
    }
    
    return null;
  }



  /**
   * Get discount code for specific source
   */
  getDiscountCodeForSource(source) {
    return this.config.sourceDiscounts[source] || this.config.discountCode;
  }








  /**
   * Set cookie
   */
  setCookie(name, value, days) {
    const expires = new Date();
    expires.setTime(expires.getTime() + (days * 24 * 60 * 60 * 1000));
    
    document.cookie = `${name}=${value};expires=${expires.toUTCString()};path=/;SameSite=Lax`;
  }

  /**
   * Get cookie value
   */
  getCookie(name) {
    const nameEQ = name + "=";
    const ca = document.cookie.split(';');
    
    for (let i = 0; i < ca.length; i++) {
      let c = ca[i];
      while (c.charAt(0) === ' ') c = c.substring(1, c.length);
      if (c.indexOf(nameEQ) === 0) return c.substring(nameEQ.length, c.length);
    }
    
    return null;
  }

  /**
   * Delete cookie
   */
  deleteCookie(name) {
    document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 UTC;path=/;`;
  }

  /**
   * Log messages (only in debug mode)
   */
  log(...args) {
    if (this.config.debug) {
      console.log('[PromoTracker]', ...args);
    }
  }

  /**
   * Clear all promotional data (useful for testing)
   */
  clearPromoData() {
    this.deleteCookie(this.config.cookieName);
    this.log('Promo data cleared');
  }

  /**
   * Get current promotional status
   */
  getPromoStatus() {
    const promoData = this.getPromoData();
    
    return {
      hasPromo: !!promoData,
      source: promoData?.source || null,
      applied: promoData?.applied || false,
      discountCode: this.config.discountCode
    };
  }
}

// Initialize when DOM is ready with default config
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    // Check if custom config exists
    const customConfig = window.promoTrackerConfig || {};
    window.promoTracker = new PromoTracker(customConfig);
  });
} else {
  // Check if custom config exists
  const customConfig = window.promoTrackerConfig || {};
  window.promoTracker = new PromoTracker(customConfig);
}

// Make PromoTracker available globally for debugging and manual initialization
window.PromoTracker = PromoTracker;
